import React, { useState } from 'react';
import { Play, AlertCircle, CheckCircle2, Loader2, Download, FileText, Info, Database } from 'lucide-react';
import { useGenerateCard35File } from '../hooks/useReports';
import DataTable from '../components/DataTable';

const RECORD_35_SPEC = [
  { no: 1, field: 'Record Type', format: '9(2)', position: '1-2', length: 2, required: true, description: 'Always equals "35"' },
  { no: 2, field: 'Action Type', format: '9(2)', position: '3-4', length: 2, required: true, description: '01-new issuance, 02-update existing' },
  { no: 3, field: 'Last Name', format: 'X(50)', position: '5-54', length: 50, required: 'Cond', description: 'Last name of cardholder' },
  { no: 4, field: 'SSN', format: '9(9)', position: '55-63', length: 9, required: 'Cond', description: 'Social Security Number' },
  { no: 5, field: 'PAN/Proxy/Client Unique ID', format: '9(19) or X(19)', position: '64-82', length: 19, required: 'Cond', description: 'Card identifier' },
  { no: 6, field: 'Residential Addr1', format: 'X(50)', position: '83-132', length: 50, required: false, description: 'Address line 1' },
  { no: 7, field: 'Residential Addr2', format: 'X(50)', position: '133-182', length: 50, required: false, description: 'Address line 2' },
  { no: 8, field: 'Residential City', format: 'X(35)', position: '183-217', length: 35, required: false, description: 'City' },
  { no: 9, field: 'Residential State', format: 'X(25)', position: '218-242', length: 25, required: false, description: 'State (2-char abbreviation)' },
  { no: 10, field: 'Residential Postal Code', format: 'X(30)', position: '243-272', length: 30, required: false, description: 'Postal/ZIP code' },
  { no: 11, field: 'Residential Country Code', format: '9(3)', position: '273-275', length: 3, required: false, description: 'ISO code (USA uses 840)' },
  { no: 12, field: 'Driver\'s License Number', format: 'X(20)', position: '276-295', length: 20, required: false, description: 'Driver\'s license number' },
  { no: 13, field: 'Driver\'s License State', format: 'X(2)', position: '296-297', length: 2, required: false, description: 'DL state' },
  { no: 14, field: 'Residential Addr3', format: 'X(50)', position: '298-347', length: 50, required: false, description: 'Address line 3' },
  { no: 15, field: 'Filler', format: 'X(10)', position: '348-357', length: 10, required: true, description: 'Filler spaces' },
  { no: 16, field: 'Card Record Status Code', format: '9(2)', position: '358-359', length: 2, required: true, description: '01-sent, 02-processed, etc.' },
  { no: 17, field: 'Processing Message', format: 'X(41)', position: '360-400', length: 41, required: false, description: 'Error message if any' },
];

const sampleQuery = `SELECT
  '35' as record_type,
  '01' as action_type,
  p.last_name,
  p.ssn,
  ca.proxy_number as pan_proxy,
  p.mailing_addr_line_1 as residential_addr1,
  p.mailing_addr_line_2 as residential_addr2,
  p.city as residential_city,
  p.mailing_state as residential_state,
  p.postal_code as residential_postal_code,
  '840' as residential_country_code,
  '' as drivers_license_number,
  '' as drivers_license_state,
  '' as residential_addr3,
  '' as filler,
  '01' as card_record_status_code,
  '' as processing_message
FROM huser.person p
INNER JOIN huser.consumer c ON p.person_id = c.person_id
JOIN fis.consumer_account ca ON ca.consumer_code = c.consumer_code
WHERE c.delete_nbr = 0
  AND ca.delete_nbr = 0`;

export default function FISCard35() {
  const [query, setQuery] = useState(sampleQuery);
  const [previewData, setPreviewData] = useState(null);
  const { mutate, data, isPending, isError, error, isSuccess, reset } = useGenerateCard35File();

  const handleExecuteQuery = () => {
    if (query.trim()) {
      setPreviewData(null);
      mutate({ query, preview: true }, {
        onSuccess: (response) => {
          if (response?.data) {
            setPreviewData(response.data);
          }
        }
      });
    }
  };

  const handleGenerateFile = () => {
    if (query.trim()) {
      mutate({ query, preview: false }, {
        onSuccess: (response) => {
          if (response?.fileContent) {
            const blob = new Blob([response.fileContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `FIS_Card35_${new Date().toISOString().split('T')[0]}.txt`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }
        }
      });
    }
  };

  const generateColumns = (data) => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      sortable: true,
    }));
  };

  const specColumns = [
    { key: 'no', label: 'No', sortable: false },
    { key: 'field', label: 'Field Name', sortable: false },
    { key: 'format', label: 'Format', sortable: false },
    { key: 'position', label: 'Position', sortable: false },
    { key: 'length', label: 'Length', sortable: false },
    { key: 'required', label: 'Required', sortable: false },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">FIS Card 35 Generator</h2>
        <p className="text-gray-500 mt-1">
          Generate Generic Batch Loader files with Record Type 35 (Optional Issue Record: Residential Address and Additional Cardholder Data)
        </p>
      </div>

      <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-medium text-amber-900">Record Type 35 Specification</h4>
          <p className="text-sm text-amber-700 mt-1">
            This generates a fixed-width (non-delimited) text file with 400-character records.
            Each record contains residential address and additional cardholder data for FIS batch processing.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Field Specification</h3>
        </div>
        <div className="overflow-x-auto max-h-64 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {specColumns.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {RECORD_35_SPEC.map((spec) => (
                <tr key={spec.no} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap">{spec.no}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-medium">{spec.field}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-mono text-xs">{spec.format}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{spec.position}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{spec.length}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      spec.required === true ? 'bg-red-100 text-red-700' :
                      spec.required === 'Cond' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {spec.required === true ? 'Yes' : spec.required === 'Cond' ? 'Cond' : 'Opt'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">SQL Query</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Enter a SELECT query that returns columns matching the Record 35 field names (record_type, action_type, last_name, ssn, pan_proxy, etc.)
        </p>

        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter your SQL query here..."
          className="w-full h-64 p-4 font-mono text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
          spellCheck="false"
        />

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm">
            {isPending && (
              <span className="flex items-center gap-2 text-amber-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </span>
            )}
            {isSuccess && data?.rowCount !== undefined && (
              <span className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                {previewData ? `Preview: ${data.rowCount} records` : `File generated with ${data.rowCount} records`}
              </span>
            )}
            {isError && (
              <span className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                {error?.message || 'Operation failed'}
              </span>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleExecuteQuery}
              disabled={!query.trim() || isPending}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4 mr-2" />
              Preview Data
            </button>
            <button
              onClick={handleGenerateFile}
              disabled={!query.trim() || isPending}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4 mr-2" />
              Generate & Download File
            </button>
          </div>
        </div>
      </div>

      {previewData && previewData.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Data Preview</h3>
            <span className="text-sm text-gray-500">{previewData.length} records</span>
          </div>
          <DataTable
            data={previewData}
            columns={generateColumns(previewData)}
            emptyMessage="No data returned from query"
          />
        </div>
      )}

      {isSuccess && data?.sampleLines && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sample Output (First 3 Lines)</h3>
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-green-400 font-mono text-xs whitespace-pre">
              {data.sampleLines.join('\n')}
            </pre>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Each line is exactly 400 characters (fixed-width format)
          </p>
        </div>
      )}
    </div>
  );
}
